import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject as RxJsSubject, forkJoin } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { BillingPeriod } from '../../../../core/interfaces/registration';
import { LearningPath } from '../../../../core/interfaces/learning-path';
import {
  Pack,
  PathLevel,
  UpsertLevelDiscount,
  UpsertLevelPrice,
  UpsertSubjectPrice,
} from '../../../../core/interfaces/pack';
import { LearningService } from '../../../../core/services/learning-path.service';
import { PackAdminService } from '../../../../core/services/pack-admin.service';
import { SwalService } from '../../../../core/services/utils/swal.service';
import { AuthService } from '../../../../core/services/utils/auth.service';
import {
  PermissionPickerComponent,
  PickerModule,
} from '../../../ui/permission-picker/permission-picker.component';
import { ReadOnlyNoticeComponent } from '../../../ui/read-only-notice/read-only-notice.component';

/** Une matière et ses trois montants possibles, pour l'édition. */
interface PriceRow {
  subjectId: number;
  subjectName: string;
  /**
   * La matière est-elle vendue ? Une matière inactive garde sa ligne — c'est
   * le seul écran, avec celui des parcours, où elle se voit — et ses tarifs
   * restent modifiables, mais elle ne compte dans aucun total : le site ne la
   * vend pas.
   */
  isActive: boolean;
  /** Montant par périodicité ; `null` = pas de tarif, donc combinaison non proposée. */
  amounts: Record<BillingPeriod, number | null>;
}

/** Les matières restent groupées par parcours : c'est ainsi qu'on les vend. */
interface PathGroup {
  pathId: number;
  pathTitle: string;
  rows: PriceRow[];
}

/**
 * Les classes d'un même cycle, pour la saisie des prix affichés.
 *
 * <p>Elles sont regroupées parce qu'il y en a une vingtaine : une liste plate
 * de « 4ème année » à « Bac » se parcourt mal, et rien n'y signale qu'on a
 * changé de cycle.</p>
 */
interface LevelGroup {
  level: PathLevel;
  paths: { id: number; title: string }[];
}

/**
 * Une classe dont le prix ne vaut pas le total de ses matières.
 *
 * <p>L'écart n'est pas qu'inesthétique : le prix de la classe s'applique à qui
 * la prend entière, la somme dès qu'on en décoche une. Quand la somme est la
 * plus élevée, **retirer une matière fait monter la facture** — l'élève paie
 * plus pour recevoir moins.</p>
 */
interface PriceGap {
  pathId: number;
  pathTitle: string;
  period: BillingPeriod;
  levelAmount: number;
  subjectsTotal: number;
  /** Toutes les matières ne sont pas tarifées : le total est donc partiel. */
  incomplete: boolean;
}

/**
 * Une classe tarifée sans être vendable pour une périodicité donnée.
 *
 * <p>Des matières ont un prix, d'autres non, et la classe n'en a pas : le
 * site ne peut ni annoncer le prix de la classe ni sommer une grille trouée,
 * donc il n'affiche rien. De l'argent a été saisi, et il ne produit aucune
 * vente — c'est le cas le plus coûteux, parce que rien à l'écran ne le
 * distinguait d'une classe correctement tarifée.</p>
 */
interface UnsellableSlot {
  pathId: number;
  pathTitle: string;
  period: BillingPeriod;
  /** Matières encore sans tarif : ce qu'il reste à saisir pour vendre. */
  missing: number;
}

/**
 * Ce qu'un onglet de cycle doit signaler quand il est fermé, du plus grave au
 * moins grave. Un seul repère par onglet : le pire l'emporte.
 */
export type LevelMark = 'unsellable' | 'gap' | 'pending';

const PERIODS: BillingPeriod[] = ['monthly', 'yearly', 'single'];
const LEVELS: PathLevel[] = ['primaire', 'college', 'secondaire'];

/**
 * En deçà de ce montant, l'écart n'est pas une décision de prix.
 *
 * <p>Un prix de classe se répartit rarement juste : 2600 sur six matières fait
 * 433,333 chacune, et leur somme retombe à 2599,998. Alerter sur deux
 * millimes apprendrait à fermer l'alerte sans la lire — et c'est alors l'écart
 * de 100 dinars qui passerait avec.</p>
 */
const GAP_TOLERANCE = 1;

/**
 * Mois facturés dans une année scolaire : de septembre à mai. Un réglage de
 * l'écran, à ajuster par formule, jamais enregistré.
 */
const DEFAULT_YEARLY_MONTHS = 9;
const MIN_YEARLY_MONTHS = 1;
const MAX_YEARLY_MONTHS = 12;

/** Prix annuel = mensuel × mois, arrondi au millime — la précision de la colonne. */
export function yearlyFromMonthly(monthly: number, months: number): number {
  return Math.round(monthly * months * 1000) / 1000;
}

/**
 * Packs commerciaux, prix par classe et grille tarifaire par matière.
 *
 * <p>Deux montants se saisissent ici, et la règle les articule : le prix
 * d'une classe est ce que paie l'élève qui la prend entière — c'est lui que
 * le site annonce ; la grille par matière est ce qu'il paie s'il en décoche.
 * Une classe laissée sans prix se vend à la somme de ses matières, d'où le
 * total affiché en pied de grille.</p>
 */
@Component({
  selector: 'app-packs',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ReadOnlyNoticeComponent, PermissionPickerComponent],
  templateUrl: './packs.component.html',
  styleUrl: './packs.component.css',
})
export class PacksComponent implements OnInit, OnDestroy {
  private readonly packsApi = inject(PackAdminService);
  private readonly learning = inject(LearningService);
  private readonly swal = inject(SwalService);
  private readonly translate = inject(TranslateService);
  private readonly destroy$ = new RxJsSubject<void>();

  /**
   * Modifier les offres et leurs prix est une permission à part de les lire. Sans `packs.write`, l'écran s'ouvre en consultation : champs
   * figés, gestes d'écriture retirés, et un bandeau qui le dit. Le back
   * revérifie : ce test n'est pas la protection, c'est l'honnêteté de l'écran.
   */
  readonly canWrite = inject(AuthService).hasPermission('packs.write');

  readonly periods = PERIODS;

  packs: Pack[] = [];
  paths: LearningPath[] = [];
  selected: Pack | null = null;

  groups: PathGroup[] = [];

  /**
   * Nombre de mois facturés dans une année scolaire — neuf par défaut, de
   * septembre à mai. Un réglage de l'écran, jamais enregistré : c'est
   * l'entrée d'un calcul, pas une règle que le serveur devrait connaître ;
   * lui facture les prix annuels tels qu'ils sont saisis.
   */
  yearlyMonths: number | null = DEFAULT_YEARLY_MONTHS;
  readonly MIN_YEARLY_MONTHS = MIN_YEARLY_MONTHS;
  readonly MAX_YEARLY_MONTHS = MAX_YEARLY_MONTHS;

  /**
   * Remplacer aussi les prix annuels déjà saisis. Hors de ce choix explicite,
   * confirmé, le remplissage ne touche qu'aux cases vides : un prix saisi est
   * une décision, pas une case à recalculer.
   */
  overwriteYearly = false;

  /**
   * Cases de prix annuel calculées depuis le mensuel (× nombre de mois), pas
   * encore enregistrées. Même rôle que `prefilled`, autre origine — et une
   * autre pastille, qui dit d'où vient le nombre.
   */
  private computed = new Set<string>();

  /** Le nombre de mois utilisé au dernier remplissage : c'est lui que les pastilles affichent, pas la saisie courante. */
  computedMonths: number | null = null;

  /** Les classes tarifables, groupées par cycle. */
  levelGroups: LevelGroup[] = [];

  /**
   * Le cycle affiché. Les trois cycles s'empilaient : une vingtaine de classes
   * à six matières faisaient une page de plus de cent lignes. Un onglet par
   * cycle, un seul rendu à la fois.
   *
   * <p>Seul l'**affichage** est par onglet. Les alertes, le calcul de l'annuel
   * et l'enregistrement couvrent toujours toutes les classes : un onglet fermé
   * n'est ni hors contrôle ni hors sauvegarde — d'où le repère qu'il porte
   * quand son cycle a quelque chose à dire.</p>
   */
  readonly activeLevel = signal<PathLevel | null>(null);

  /**
   * Classes dont le prix ne vaut pas le total de leurs matières.
   *
   * <p>Recalculé à chaque saisie, pour que le bandeau suive ce qui est à
   * l'écran et non ce qui est enregistré — l'écart se voit pendant qu'on le
   * crée, pas après l'avoir publié.</p>
   */
  gaps: PriceGap[] = [];

  /**
   * Classes tarifées dont aucun prix n'atteindra le site.
   *
   * <p>Distinct des écarts : un écart annonce deux prix qui divergent, ceci
   * annonce qu'il n'y en aura aucun.</p>
   */
  unsellable: UnsellableSlot[] = [];

  /**
   * Cases pré-remplies avec le total des matières, pas encore enregistrées.
   *
   * <p>Une classe sans prix ne se vend qu'à la somme de ses matières, et
   * seulement si elles sont <b>toutes</b> tarifées. Laisser la case vide
   * revenait donc à ne rien publier dès qu'il en manquait une : la 5ème
   * année totalisait 80 د/mois sur quatre matières, la cinquième n'avait pas
   * de tarif, et le site n'affichait aucun prix.</p>
   *
   * <p>La case porte désormais ce total. Ce n'est pas une valeur enregistrée
   * — d'où cet ensemble, qui la distingue à l'écran et rappelle qu'il reste
   * à cliquer « mettre à jour ».</p>
   */
  private prefilled = new Set<string>();

  /**
   * Prix affichés sur le site : un montant par classe et périodicité. Ce sont
   * eux que voit un visiteur, et ce qui est facturé à qui prend toute la
   * classe. Une case laissée vide fait retomber la classe sur la somme de ses
   * matières.
   */
  display: Record<string, number | null> = {};

  /**
   * Remise visant une classe, en pourcentage, par identifiant de classe.
   *
   * <p>Elle ne dépend pas de la périodicité : c'est la classe qu'elle vise,
   * et un taux par périodicité aurait voulu dire trois remises à tenir
   * cohérentes pour un seul geste commercial.</p>
   *
   * <p>La case vide et le zéro ne disent pas la même chose, et c'est la
   * distinction qui compte ici : vide, la classe retombe sur les remises du
   * pack ; à zéro, elle les refuse. C'est la seule façon d'exclure une
   * classe d'une remise générale.</p>
   */
  discounts: Record<number, number | null> = {};

  /**
   * Les fonctionnalités de l'espace élève qu'un pack peut ouvrir, et celles
   * qu'ouvre le pack sélectionné (SKILL2ERA-137). Elles s'enregistrent à part
   * des prix : c'est un autre geste, et le serveur les applique aux élèves du
   * pack dès leur requête suivante.
   */
  /** Le catalogue, sous la forme du sélecteur partagé avec « Profils et accès ». */
  featureModules: PickerModule[] = [];
  packFeatures = new Set<string>();
  isSavingFeatures = false;

  /**
   * Les cases reflètent-elles le pack sélectionné ? Faux tant que sa lecture
   * n'a pas répondu, ou si elle a échoué : enregistrer alors enverrait une
   * liste vide, et fermerait tout aux élèves du pack.
   */
  featuresLoaded = false;

  /** Cases modifiées et non enregistrées — couvertes par la confirmation au changement de pack. */
  featuresDirty = false;

  /** Le catalogue n'a pas pu être lu : le panneau le dit, le reste de l'écran fonctionne. */
  featuresUnavailable = false;

  isLoading = false;
  hasError = false;
  isLoadingGrid = false;
  isSaving = false;
  isDirty = false;

  /** Classe dont le bouton de mise à jour tourne, pour ne l'animer que lui. */
  savingPathId: number | null = null;

  /**
   * Classes dont les matières sont **repliées** sous leur ligne.
   *
   * <p>C'est le repli qu'on retient, et non l'ouverture : tout est ouvert par
   * défaut, et une liste des classes ouvertes devrait être remplie à mesure
   * que la grille arrive — une classe chargée après coup s'afficherait donc
   * fermée, sans que rien l'explique.</p>
   *
   * <p>Ouvrir tout se paie en longueur : une vingtaine de classes à six
   * matières font plus de cent lignes. C'est assumé — on vient ici pour
   * comparer les prix des matières à celui de leur classe, et les cacher
   * derrière un clic revenait à masquer ce qu'on est venu lire.</p>
   */
  private collapsed = new Set<number>();

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private load(): void {
    this.isLoading = true;
    this.hasError = false;

    // Packs et parcours arrivent ensemble : la grille n'a de sens qu'avec
    // la liste complète des matières, y compris celles encore sans tarif —
    // et celles qui sont inactives, que seul le référentiel entier rend.
    // Le catalogue des accès à part : son échec ne doit pas fermer l'écran
    // des prix, qui n'en dépend pas.
    this.packsApi
      .getStudentFeatures()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) =>
          (this.featureModules = (res.data ?? []).map(({ features, ...module }) => ({ ...module, permissions: features }))),
        error: () => (this.featuresUnavailable = true),
      });

    forkJoin({
      packs: this.packsApi.getAll(),
      paths: this.learning.getReferential(),
    })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: ({ packs, paths }) => {
          this.packs = packs.data ?? [];
          this.paths = paths.data ?? [];
          this.buildLevelGroups();
          if (this.packs.length > 0) this.select(this.packs[0]);
        },
        error: () => (this.hasError = true),
      });
  }

  select(pack: Pack): void {
    if ((this.isDirty || this.featuresDirty) && !confirm(this.translate.instant('PACKS.DISCARD_CHANGES'))) return;

    this.selected = pack;
    this.isDirty = false;
    this.loadFeatures(pack.id);
    this.groups = [];
    this.computed.clear();
    this.computedMonths = null;
    this.gaps = [];
    this.unsellable = [];
    this.prefilled.clear();
    // Les replis appartiennent au pack qu'on quitte : les garder ferait
    // arriver le suivant avec des classes fermées sans raison visible.
    this.collapsed.clear();

    this.display = {};
    for (const price of pack.levelPrices ?? []) {
      this.display[this.key(price.learningPathId, price.billingPeriod)] = price.amount;
    }

    this.discounts = {};
    for (const remise of pack.levelDiscounts ?? []) {
      this.discounts[remise.learningPathId] = remise.discountPercent;
    }

    // Un pack au forfait ou sur devis n'a pas de grille par matière : seuls
    // ses prix affichés se saisissent.
    if (pack.pricingMode !== 'per_subject') return;

    this.isLoadingGrid = true;
    this.packsApi
      .getSubjectPrices(pack.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoadingGrid = false)),
      )
      .subscribe({
        next: (res) => {
          this.buildGrid(res.data ?? []);
          // Le pré-remplissage précède le contrôle : c'est la grille telle
          // qu'elle sera publiée qu'il faut vérifier, pas celle d'avant.
          this.prefillFromSubjects();
          // Un écart déjà enregistré doit se signaler à l'ouverture : il a été
          // saisi un autre jour, et personne ne rouvrira l'écran pour vérifier.
          this.refreshGaps();
          this.announceGaps();
        },
        error: (err) => this.swal.error(this.messageOf(err, 'PACKS.ERROR_PRICES')),
      });
  }

  /**
   * Construit la grille à partir de **toutes les matières** des parcours, et
   * non des seules lignes tarifées : sans cela, une matière nouvellement créée
   * resterait invisible et donc impossible à tarifer.
   *
   * <p>Une matière inactive y a sa ligne, signalée : on la tarife avant de
   * l'ouvrir à la vente, et on voit ce qu'elle vaudra en la réactivant. Elle
   * était masquée, et ses tarifs voyageaient dans une liste à part pour ne
   * pas être effacés à l'enregistrement — le serveur remplace la grille en
   * bloc. Ils voyagent désormais avec leur ligne, comme tous les autres : la
   * garantie est la même, sans mécanisme caché.</p>
   */
  private buildGrid(prices: { subjectId: number; billingPeriod: BillingPeriod; amount: number }[]): void {
    const byKey = new Map(prices.map((p) => [`${p.subjectId}:${p.billingPeriod}`, p.amount]));

    this.groups = this.paths
      .map((path) => ({
        pathId: path.id,
        pathTitle: path.title,
        rows: (path.subjects ?? [])
          .map((s) => ({
            subjectId: s.id,
            subjectName: s.name,
            // La même règle que le catalogue public : seule une matière
            // explicitement active est vendue. `!== false` laissait passer
            // une matière que l'API ne qualifiait pas.
            isActive: s.isActive === true,
            amounts: {
              monthly: byKey.get(`${s.id}:monthly`) ?? null,
              yearly: byKey.get(`${s.id}:yearly`) ?? null,
              single: byKey.get(`${s.id}:single`) ?? null,
            } as Record<BillingPeriod, number | null>,
          })),
      }))
      .filter((g) => g.rows.length > 0);
  }

  /**
   * Les matières d'une classe que le site vend : c'est sur elles, et sur elles
   * seules, que portent les totaux, les écarts, les classes invendables et le
   * calcul des prix annuels. Une matière inactive est à l'écran, pas au
   * catalogue.
   */
  private sold(group: PathGroup): PriceRow[] {
    return group.rows.filter((r) => r.isActive);
  }

  /**
   * Pose dans chaque case de prix vide le total des matières de sa classe.
   *
   * <p>Sans ce report, une classe dont il manque un tarif de matière ne
   * s'affichait nulle part : le site ne somme que les grilles complètes, et
   * la case vide ne lui donnait rien d'autre. La 5ème année totalisait
   * 80 د/mois sur quatre matières et se vendait zéro.</p>
   *
   * <p>Le total proposé est celui des matières <b>tarifées</b>. Les autres
   * sont donc comprises dans ce prix : c'est un choix commercial, et le
   * laisser à l'administration plutôt que de le deviner voulait dire ne rien
   * publier du tout.</p>
   *
   * <p>Rien n'est écrit là où une valeur existe déjà : le prix saisi fait
   * foi, et l'écraser par une somme reviendrait à défaire une décision de
   * prix à chaque ouverture de l'écran.</p>
   *
   * <p>Le pré-remplissage ne rend pas l'écran « modifié » : il n'a rien
   * décidé, et faire demander « abandonner les modifications ? » à chaque
   * changement de pack apprendrait à répondre oui sans lire.</p>
   */
  private prefillFromSubjects(periods: readonly BillingPeriod[] = PERIODS): void {
    for (const group of this.levelGroups) {
      for (const path of group.paths) {
        for (const period of periods) {
          const cle = this.key(path.id, period);
          if (this.display[cle] != null) continue;

          const total = this.subjectsTotal(path.id, period);
          if (total == null || total <= 0) continue;

          this.display[cle] = total;
          this.prefilled.add(cle);
        }
      }
    }
  }

  /** Cette case porte-t-elle un total proposé, pas encore enregistré ? */
  isPrefilled(pathId: number, period: BillingPeriod): boolean {
    return this.prefilled.has(this.key(pathId, period));
  }

  /** Des totaux attendent d'être enregistrés. */
  get hasPrefilled(): boolean {
    return this.prefilled.size > 0;
  }

  /** Cette case de prix de classe a été calculée depuis le mensuel, et attend d'être enregistrée. */
  isComputed(pathId: number, period: BillingPeriod): boolean {
    return this.computed.has(this.key(pathId, period));
  }

  /** Clé d'une case de la grille par matière — préfixée, pour ne jamais croiser une clé de classe. */
  rowKey(subjectId: number, period: BillingPeriod): string {
    return `s:${subjectId}:${period}`;
  }

  /** Cette case de matière a été calculée depuis le mensuel, et attend d'être enregistrée. */
  isRowComputed(subjectId: number, period: BillingPeriod): boolean {
    return this.computed.has(this.rowKey(subjectId, period));
  }

  /** Des prix annuels calculés attendent d'être enregistrés. */
  get hasComputed(): boolean {
    return this.computed.size > 0;
  }

  /** Le nombre de mois saisi est exploitable : un entier entre les bornes. */
  get yearlyMonthsValid(): boolean {
    const n = Number(this.yearlyMonths);
    return Number.isInteger(n) && n >= MIN_YEARLY_MONTHS && n <= MAX_YEARLY_MONTHS;
  }

  /**
   * Pose le prix annuel = mensuel × nombre de mois dans les cases annuelles
   * vides, classes et matières — et dans les cases saisies seulement si on
   * l'a demandé, après confirmation. Rien ne part au serveur : c'est une
   * proposition, comme le report des totaux, à enregistrer ensuite.
   *
   * <p>Une case de classe se calcule depuis son propre mensuel quand elle en a
   * un : si ce mensuel diffère du total des matières, c'est une décision de
   * prix, et l'annuel la suit. Sans mensuel propre, la classe reçoit, comme à
   * l'ouverture, le total de ses matières — désormais tarifées à l'année.</p>
   */
  async fillYearly(): Promise<void> {
    const pack = this.selected;
    if (!pack || !this.yearlyMonthsValid) return;
    const months = Number(this.yearlyMonths);

    // Une case « décidée » porte une valeur qui n'est ni un total reporté ni
    // un calcul précédent : une saisie, ou un prix déjà enregistré. Les
    // propositions, elles, se recalculent sans rien demander.
    const decided = (cle: string, value: number | null | undefined): boolean =>
      value != null && !this.prefilled.has(cle) && !this.computed.has(cle);

    const classCells = this.levelGroups
      .flatMap((g) => g.paths)
      .map((p) => {
        const cle = this.key(p.id, 'yearly');
        return {
          cle,
          monthly: Number(this.display[this.key(p.id, 'monthly')] ?? 0),
          decided: decided(cle, this.display[cle]),
        };
      })
      .filter((c) => c.monthly > 0);
    const rows = this.groups
      .flatMap((g) => this.sold(g))
      .filter((r) => Number(r.amounts.monthly ?? 0) > 0)
      .map((r) => {
        const cle = this.rowKey(r.subjectId, 'yearly');
        return { row: r, cle, decided: decided(cle, r.amounts.yearly) };
      });

    if (this.overwriteYearly) {
      const replaced = classCells.filter((c) => c.decided).length + rows.filter((r) => r.decided).length;
      if (replaced > 0) {
        const ok = await this.swal.confirmAsync(
          this.translate.instant('PACKS.FILL_YEARLY_CONFIRM', { n: replaced, mois: months }),
        );
        // Le dialogue a pu laisser passer un changement de pack : rien ne se
        // pose dans une autre grille que celle du clic.
        if (!ok || this.selected !== pack) return;
      }
    }

    let filled = 0;
    for (const c of classCells) {
      if (c.decided && !this.overwriteYearly) continue;
      this.display[c.cle] = yearlyFromMonthly(c.monthly, months);
      this.prefilled.delete(c.cle);
      this.computed.add(c.cle);
      filled++;
    }
    for (const r of rows) {
      if (r.decided && !this.overwriteYearly) continue;
      r.row.amounts.yearly = yearlyFromMonthly(Number(r.row.amounts.monthly), months);
      this.computed.add(r.cle);
      filled++;
    }

    // Une classe sans mensuel propre reçoit, comme à l'ouverture, le total
    // annuel de ses matières — qui viennent d'en recevoir un. L'annuel seul :
    // un mensuel vidé exprès est une décision, pas une case à reboucher.
    const before = this.prefilled.size;
    this.prefillFromSubjects(['yearly']);
    filled += this.prefilled.size - before;

    if (filled === 0) {
      this.swal.info(this.translate.instant('PACKS.FILL_YEARLY_NOTHING'));
      return;
    }

    this.computedMonths = months;
    this.isDirty = true;
    this.refreshGaps();
    this.swal.success(this.translate.instant('PACKS.FILL_YEARLY_DONE', { n: filled }));
  }

  /**
   * @param cle La case modifiée, pour une case de prix de classe. La saisie
   *   reprend la main sur le total proposé : la valeur devient une décision,
   *   et l'écran cesse de la présenter comme une suggestion.
   */
  onAmountChange(cle?: string): void {
    if (cle) {
      this.prefilled.delete(cle);
      this.computed.delete(cle);
    }
    this.isDirty = true;
    this.refreshGaps();
  }

  /** Total du parcours : ce que paierait un élève prenant toutes les matières vendues. */
  total(group: PathGroup, period: BillingPeriod): number {
    return this.sold(group).reduce((sum, r) => sum + (r.amounts[period] ?? 0), 0);
  }

  hasAny(group: PathGroup, period: BillingPeriod): boolean {
    return this.sold(group).some((r) => r.amounts[period] != null);
  }

  /** Toutes les matières vendues de la classe sont tarifées pour cette périodicité. */
  private allPriced(group: PathGroup, period: BillingPeriod): boolean {
    const sold = this.sold(group);
    return sold.length > 0 && sold.every((r) => r.amounts[period] != null);
  }

  /**
   * Les classes dont le prix ne vaut pas le total de leurs matières.
   *
   * <p>La comparaison porte sur ce que l'écran affiche : dès qu'une matière
   * est tarifée, le total s'affiche sous la grille, et il doit valoir le prix
   * de la classe. Une grille partielle n'y échappe pas — un total de 100 en
   * face d'un prix de 150 est précisément ce qu'il faut signaler, et l'écarter
   * sous prétexte qu'il manque des tarifs laissait passer le cas le plus
   * courant.</p>
   *
   * <p>Une périodicité où <b>aucune</b> matière n'est tarifée reste hors
   * comparaison : la grille n'y dit rien, et le prix de la classe s'y applique
   * seul, comme prévu. Y annoncer un écart contre zéro serait un reproche fait
   * à une case jamais remplie.</p>
   *
   * <p>Lu depuis l'écran et non depuis le serveur : l'écart doit se voir
   * pendant qu'on le saisit, pas une fois publié.</p>
   */
  private computeGaps(): PriceGap[] {
    const gaps: PriceGap[] = [];

    for (const group of this.levelGroups) {
      for (const path of group.paths) {
        const grid = this.groups.find((g) => g.pathId === path.id);
        if (!grid) continue;

        for (const period of PERIODS) {
          const levelAmount = this.display[this.key(path.id, period)];
          if (levelAmount == null || Number(levelAmount) <= 0) continue;
          if (!this.hasAny(grid, period)) continue;

          const subjectsTotal = this.total(grid, period);
          if (Math.abs(Number(levelAmount) - subjectsTotal) > GAP_TOLERANCE) {
            gaps.push({
              pathId: path.id,
              pathTitle: path.title,
              period,
              levelAmount: Number(levelAmount),
              subjectsTotal,
              incomplete: !this.allPriced(grid, period),
            });
          }
        }
      }
    }

    return gaps;
  }

  private refreshGaps(): void {
    this.gaps = this.computeGaps();
    this.unsellable = this.computeUnsellable();
  }

  /**
   * Vrai si retirer une matière ferait monter la facture.
   *
   * <p>Seulement sur une grille complète : incomplète, elle fait de toute
   * façon retomber l'inscription sur le prix de la classe, donc rien ne monte.
   * Annoncer l'inversion là serait une alarme fausse.</p>
   */
  isInversion(gap: PriceGap): boolean {
    return !gap.incomplete && gap.subjectsTotal > gap.levelAmount;
  }

  /**
   * Cette classe est-elle à reprendre ? Marque sa ligne dans la grille.
   *
   * <p>Écart de prix ou classe invendable : les deux méritent le même
   * triangle. Les distinguer d'un coup d'œil demanderait deux icônes là où la
   * ligne en porte déjà une, et le détail est dans le bandeau.</p>
   */
  classHasGap(pathId: number): boolean {
    return (
      this.gaps.some((gap) => gap.pathId === pathId) ||
      this.unsellable.some((slot) => slot.pathId === pathId)
    );
  }

  /** Les matières de cette classe sont-elles visibles sous sa ligne ? */
  isExpanded(pathId: number): boolean {
    return !this.collapsed.has(pathId);
  }

  toggleExpand(pathId: number): void {
    if (!this.collapsed.delete(pathId)) this.collapsed.add(pathId);
  }

  /** Les matières d'une classe, pour les lignes dépliées. */
  rowsOf(pathId: number): PriceRow[] {
    return this.groups.find((g) => g.pathId === pathId)?.rows ?? [];
  }

  /**
   * Total des matières d'une classe, ou `null` si aucune n'est tarifée.
   *
   * <p>C'est la somme de ce qui est saisi, et rien de plus : une matière sans
   * tarif y compte pour zéro. Elle sert à comparer, pas à annoncer un prix —
   * voir {@link fallbackTotal} pour le montant que le site appliquera.</p>
   */
  subjectsTotal(pathId: number, period: BillingPeriod): number | null {
    const group = this.groups.find((g) => g.pathId === pathId);
    if (!group || !this.hasAny(group, period)) return null;
    return this.total(group, period);
  }

  /**
   * Ce que le site facturera faute de prix de classe, ou `null` s'il ne
   * facturera rien.
   *
   * <p>La somme des matières ne remplace le prix de la classe que si
   * <b>toutes</b> sont tarifées. Le site refuse de sommer une grille
   * incomplète, et il a raison : additionner les seules lignes remplies
   * reviendrait à offrir les autres.</p>
   *
   * <p>C'est cette valeur, et non {@link subjectsTotal}, que la case vide
   * affiche en filigrane. L'écran promettait auparavant la somme partielle —
   * 80 د/mois annoncés ici pour une 5ème année dont deux matières n'avaient
   * pas de tarif, quand le site n'y affichait aucun prix. Un filigrane qui
   * annonce un montant que le visiteur ne verra jamais est pire que pas de
   * filigrane du tout : il fait croire la classe en vente.</p>
   */
  fallbackTotal(pathId: number, period: BillingPeriod): number | null {
    const group = this.groups.find((g) => g.pathId === pathId);
    if (!group || !this.allPriced(group, period)) return null;
    return this.total(group, period);
  }

  /**
   * Cette classe est-elle tarifée sans être vendable pour cette périodicité ?
   *
   * <p>Trois conditions réunies : pas de prix de classe, des matières
   * tarifées, et d'autres qui ne le sont pas. Le site n'a alors ni prix de
   * classe à annoncer ni grille complète à sommer — la périodicité disparaît
   * de la page des offres, et l'inscription devient impossible.</p>
   *
   * <p>Une périodicité où <b>rien</b> n'est tarifé n'est pas concernée : ne
   * pas proposer le paiement unique est une décision, pas un oubli. Ce qui
   * est signalé, c'est du prix saisi qui ne produit aucune vente.</p>
   */
  isUnsellable(pathId: number, period: BillingPeriod): boolean {
    const levelAmount = this.display[this.key(pathId, period)];
    if (levelAmount != null && Number(levelAmount) > 0) return false;

    const group = this.groups.find((g) => g.pathId === pathId);
    if (!group) return false;

    return this.hasAny(group, period) && !this.allPriced(group, period);
  }

  /**
   * Les couples (classe, périodicité) tarifés mais invendables.
   *
   * <p>Recalculé comme les écarts, à chaque saisie : la case qu'on vide à
   * l'instant peut rendre sa classe invendable, et c'est maintenant qu'il
   * faut le dire.</p>
   */
  private computeUnsellable(): UnsellableSlot[] {
    const slots: UnsellableSlot[] = [];

    for (const group of this.levelGroups) {
      for (const path of group.paths) {
        for (const period of PERIODS) {
          if (!this.isUnsellable(path.id, period)) continue;

          const grid = this.groups.find((g) => g.pathId === path.id)!;
          slots.push({
            pathId: path.id,
            pathTitle: path.title,
            period,
            missing: this.sold(grid).filter((r) => r.amounts[period] == null).length,
          });
        }
      }
    }

    return slots;
  }

  /** Écart de cette classe pour cette périodicité, s'il y en a un. */
  gapFor(pathId: number, period: BillingPeriod): PriceGap | undefined {
    return this.gaps.find((gap) => gap.pathId === pathId && gap.period === period);
  }

  /** Au moins un écart où retirer une matière ferait monter la facture. */
  get hasInversion(): boolean {
    return this.gaps.some((gap) => this.isInversion(gap));
  }

  /** Au moins un écart calculé sur une grille dont des matières sont sans tarif. */
  get hasIncomplete(): boolean {
    return this.gaps.some((gap) => gap.incomplete);
  }

  /**
   * Signale par une alerte ce qui cloche dans la tarification.
   *
   * <p>Un bandeau permanent le rappelle dans l'écran, mais il se lit quand on
   * le cherche : l'alerte, elle, arrive au moment où la décision se prend —
   * en ouvrant le pack, et juste après un enregistrement.</p>
   *
   * <p>Deux défauts, une seule fenêtre : deux `Swal` successifs se recouvrent,
   * et c'est le second qu'on lit. Ils ne disent pourtant pas la même chose —
   * un écart annonce deux prix qui divergent, une classe invendable annonce
   * qu'il n'y en aura aucun.</p>
   *
   * @param gaps Les écarts à annoncer. Une mise à jour de classe ne signale
   *   que la sienne : lui faire rappeler les autres noierait celle qu'on vient
   *   de saisir.
   * @param slots Les classes invendables à annoncer, filtrées de même.
   */
  private announceGaps(
    gaps: PriceGap[] = this.gaps,
    slots: UnsellableSlot[] = this.unsellable,
  ): void {
    if (gaps.length === 0 && slots.length === 0) return;

    const corps = [this.gapsHtml(gaps), this.unsellableHtml(slots)]
      .filter((bloc) => bloc !== '')
      .join('');

    // Le titre nomme le défaut quand il n'y en a qu'un ; avec les deux, il
    // renvoie au détail plutôt que d'en taire la moitié.
    const titre =
      gaps.length > 0 && slots.length > 0
        ? 'PACKS.PROBLEMS_TITLE'
        : gaps.length > 0
          ? 'PACKS.GAP_TITLE'
          : 'PACKS.UNSELLABLE_TITLE';

    this.swal.warningHtml(corps, this.translate.instant(titre));
  }

  /** Le bloc « prix de classe et total des matières divergent », ou rien. */
  private gapsHtml(gaps: PriceGap[]): string {
    if (gaps.length === 0) return '';

    const lignes = gaps
      .map((gap) => {
        const texte = this.translate.instant('PACKS.GAP_LINE', {
          classe: gap.pathTitle,
          periode: this.translate.instant('PACKS.PERIOD_' + gap.period.toUpperCase()),
          prix: gap.levelAmount,
          total: gap.subjectsTotal,
        });
        return `<li>${this.escape(texte)}</li>`;
      })
      .join('');

    const intro = this.escape(this.translate.instant('PACKS.GAP_INTRO'));
    // L'avertissement d'inversion ne vaut que pour les écarts annoncés : le
    // rappeler sur une classe qui n'est pas concernée serait faux.
    const inversion = gaps.some((gap) => this.isInversion(gap))
      ? `<p><b>${this.escape(this.translate.instant('PACKS.GAP_INVERSION'))}</b></p>`
      : '';

    return `<p>${intro}</p>${this.list(lignes)}${inversion}`;
  }

  /** Le bloc « rien ne s'affichera sur le site », ou rien. */
  private unsellableHtml(slots: UnsellableSlot[]): string {
    if (slots.length === 0) return '';

    const lignes = slots
      .map((slot) => {
        const texte = this.translate.instant('PACKS.UNSELLABLE_LINE', {
          classe: slot.pathTitle,
          periode: this.translate.instant('PACKS.PERIOD_' + slot.period.toUpperCase()),
          manquantes: slot.missing,
        });
        return `<li>${this.escape(texte)}</li>`;
      })
      .join('');

    const intro = this.escape(this.translate.instant('PACKS.UNSELLABLE_INTRO'));
    return `<p><b>${intro}</b></p>${this.list(lignes)}`;
  }

  /**
   * Une liste à puces lisible dans une fenêtre Swal.
   *
   * <p>`text-align:start` et non `left` : l'interface est en arabe par
   * défaut, et une puce alignée à gauche dans un texte de droite à gauche se
   * détache du paragraphe qui l'introduit.</p>
   */
  private list(items: string): string {
    return `<ul style="text-align:start;margin:0 0 .75em;padding-inline-start:1.2em">${items}</ul>`;
  }

  /**
   * Neutralise le HTML d'un libellé.
   *
   * <p>Les titres de classes viennent de la base et sont saisis à la main :
   * les concaténer dans du HTML reviendrait à exécuter ce qu'un administrateur
   * y aurait collé.</p>
   */
  private escape(value: string): string {
    return value.replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
  }


  /**
   * Les classes tarifables, groupées par cycle.
   *
   * <p>Une classe sans cycle est écartée : le site range ses offres par
   * cycle, et une classe qu'aucun onglet ne contient n'y serait jamais
   * visible — son prix ne servirait à rien.</p>
   */
  private buildLevelGroups(): void {
    this.levelGroups = LEVELS
      .map((level) => ({
        level,
        paths: this.paths
          .filter((p) => p.level?.toLowerCase() === level)
          .map((p) => ({ id: p.id, title: p.title })),
      }))
      .filter((g) => g.paths.length > 0);

    // L'onglet ouvert le reste tant que son cycle existe ; sinon, le premier.
    if (!this.levelGroups.some((g) => g.level === this.activeLevel())) {
      this.activeLevel.set(this.levelGroups[0]?.level ?? null);
    }
  }

  /** Le cycle dont les classes sont à l'écran. */
  get activeGroup(): LevelGroup | undefined {
    return this.levelGroups.find((g) => g.level === this.activeLevel());
  }

  selectLevel(level: PathLevel): void {
    this.activeLevel.set(level);
  }

  /**
   * Le repère d'un onglet : ce que les bandeaux signalent pour ce cycle.
   *
   * <p>C'est la contrepartie du repli. Les bandeaux nomment les classes, mais
   * la ligne en défaut peut être dans un onglet fermé : sans repère, on
   * lirait « 3ème année — écart » sans savoir où aller la corriger.</p>
   */
  levelMark(level: PathLevel): LevelMark | null {
    const group = this.levelGroups.find((g) => g.level === level);
    if (!group) return null;
    const ids = new Set(group.paths.map((p) => p.id));

    if (this.unsellable.some((slot) => ids.has(slot.pathId))) return 'unsellable';
    if (this.gaps.some((gap) => ids.has(gap.pathId))) return 'gap';
    if (this.hasPendingIn(ids)) return 'pending';
    return null;
  }

  /** Une proposition non enregistrée — total reporté ou annuel calculé — dans ces classes ? */
  private hasPendingIn(pathIds: ReadonlySet<number>): boolean {
    const classKey = (cle: string) => pathIds.has(Number(cle.split(':')[0]));
    for (const cle of this.prefilled) if (classKey(cle)) return true;

    // Les annuels calculés portent aussi sur des matières (clés « s:… ») :
    // elles se rattachent au cycle par leur classe.
    const subjects = new Set(
      this.groups.filter((g) => pathIds.has(g.pathId)).flatMap((g) => g.rows.map((r) => r.subjectId)),
    );
    for (const cle of this.computed) {
      if (cle.startsWith('s:') ? subjects.has(Number(cle.split(':')[1])) : classKey(cle)) return true;
    }
    return false;
  }

  /**
   * Flèches, Début et Fin sur la liste d'onglets. Le sens des flèches suit
   * celui de la lecture : en arabe, « suivant » est à gauche.
   */
  onTabKeydown(event: KeyboardEvent, index: number): void {
    const tab = event.currentTarget as HTMLElement;
    const list = tab.closest('[role="tablist"]');
    if (!list) return;

    const rtl = getComputedStyle(list).direction === 'rtl';
    const last = this.levelGroups.length - 1;
    const step: Record<string, number> = {
      ArrowRight: rtl ? -1 : 1,
      ArrowLeft: rtl ? 1 : -1,
    };

    let next: number;
    if (event.key in step) next = (index + step[event.key] + last + 1) % (last + 1);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    else return;

    event.preventDefault();
    this.selectLevel(this.levelGroups[next].level);
    // L'onglet activé reçoit le focus : c'est lui qui porte le tabindex.
    list.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
  }

  /** Clé de la grille des prix affichés : un montant par (classe, périodicité). */
  key(pathId: number, period: BillingPeriod): string {
    return `${pathId}:${period}`;
  }

  /**
   * Met à jour une classe depuis sa ligne.
   *
   * <p>Les deux grilles partent ensemble : depuis que les matières se
   * saisissent sous la ligne de leur classe, n'en enregistrer qu'une perdrait
   * l'autre sans le dire. Et les deux endpoints remplacent <b>en bloc</b> :
   * n'envoyer que cette classe supprimerait le prix de toutes les autres, donc
   * chacun reçoit la grille entière telle qu'elle est à l'écran.</p>
   *
   * <p>Ce que le bouton cible, c'est la vérification qui suit : l'alerte ne
   * porte que sur la classe mise à jour, là où le bouton global les rappelle
   * toutes.</p>
   */
  updateClass(pathId: number): void {
    this.save(pathId);
  }

  /**
   * Enregistre les prix par classe et la grille par matière.
   *
   * @param onlyPathId Si fourni, l'alerte d'écart ne portera que sur cette
   *   classe. L'enregistrement, lui, couvre toujours toute la grille.
   */
  save(onlyPathId?: number): void {
    const pack = this.selected;
    if (!pack) return;

    const levelPrices: UpsertLevelPrice[] = [];
    for (const group of this.levelGroups) {
      for (const path of group.paths) {
        for (const period of PERIODS) {
          const amount = this.display[this.key(path.id, period)];
          if (amount != null && amount > 0) {
            levelPrices.push({ learningPathId: path.id, billingPeriod: period, amount: Number(amount) });
          }
        }
      }
    }

    // Les cellules vides valent suppression de la ligne correspondante. Les
    // matières inactives partent avec les autres : le remplacement est en
    // bloc, et toutes les matières ont leur ligne dans `groups` — y compris
    // celles d'un parcours sans cycle, que la grille n'affiche pas.
    const subjectPrices: UpsertSubjectPrice[] = this.groups.flatMap((g) =>
      g.rows.flatMap((r) =>
        PERIODS.filter((p) => r.amounts[p] != null && r.amounts[p]! > 0).map((p) => ({
          subjectId: r.subjectId,
          billingPeriod: p,
          amount: Number(r.amounts[p]),
        })),
      ),
    );

    // Une case vide vaut suppression de la remise — la classe retombe alors
    // sur celles du pack. Zéro, lui, part : il dit « pas de remise ici », et
    // le filtrer reviendrait à rendre à la classe la remise générale qu'on
    // vient justement de lui retirer.
    const levelDiscounts: UpsertLevelDiscount[] = [];
    for (const group of this.levelGroups) {
      for (const path of group.paths) {
        const percent = this.discounts[path.id];
        if (percent != null && String(percent) !== '') {
          levelDiscounts.push({ learningPathId: path.id, discountPercent: Number(percent) });
        }
      }
    }

    this.isSaving = true;
    this.savingPathId = onlyPathId ?? null;

    // Une formule au forfait n'a pas de grille par matière, et le serveur
    // refuse qu'on lui en envoie une : on ne lui propose donc que ses prix de
    // classe, plutôt que de lui poster un tableau vide qu'il rejetterait.
    const requests = {
      level: this.packsApi.replaceLevelPrices(pack.id, levelPrices),
      discounts: this.packsApi.replaceLevelDiscounts(pack.id, levelDiscounts),
      ...(pack.pricingMode === 'per_subject'
        ? { subjects: this.packsApi.replaceSubjectPrices(pack.id, subjectPrices) }
        : {}),
    };

    forkJoin(requests)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSaving = false;
          this.savingPathId = null;
        }),
      )
      .subscribe({
        next: ({ level, discounts, subjects }) => {
          pack.levelPrices = level.data ?? [];
          pack.levelDiscounts = discounts.data ?? [];
          if (subjects) this.buildGrid(subjects.data ?? []);
          this.isDirty = false;
          // Les totaux proposés viennent d'être envoyés : ils ne sont plus
          // des propositions, et continuer à les signaler ferait chercher un
          // enregistrement déjà fait. La sauvegarde est globale, il n'y a donc
          // pas de reste à trier.
          this.prefilled.clear();
          this.computed.clear();
          this.computedMonths = null;
          this.swal.success(this.translate.instant('PACKS.SAVED'));
          this.refreshGaps();
          this.announceGaps(
            onlyPathId == null ? this.gaps : this.gaps.filter((g) => g.pathId === onlyPathId),
            onlyPathId == null
              ? this.unsellable
              : this.unsellable.filter((s) => s.pathId === onlyPathId),
          );
        },
        error: (err) => this.swal.error(this.messageOf(err, 'PACKS.ERROR_SAVE')),
      });
  }

  savePack(): void {
    if (!this.selected) return;
    const pack = this.selected;

    this.isSaving = true;
    this.packsApi
      .update(pack.id, {
        nameAr: pack.nameAr,
        nameFr: pack.nameFr,
        nameEn: pack.nameEn,
        displayOrder: pack.displayOrder,
        isHighlighted: pack.isHighlighted,
        isActive: pack.isActive,
        requiresAllSubjects: pack.requiresAllSubjects,
        // Le serveur remet la remise à zéro pour une formule vendue d'un
        // bloc ; l'envoyer quand même laisserait croire qu'elle a été
        // enregistrée jusqu'au prochain rechargement.
        allSubjectsDiscountPercent: pack.requiresAllSubjects
          ? 0
          : Number(pack.allSubjectsDiscountPercent ?? 0),
        partialSubjectsDiscountPercent: pack.requiresAllSubjects
          ? 0
          : Number(pack.partialSubjectsDiscountPercent ?? 0),
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe({
        next: (res) => {
          const index = this.packs.findIndex((p) => p.id === res.data.id);
          if (index >= 0) this.packs[index] = res.data;
          this.selected = res.data;
          this.swal.success(this.translate.instant('PACKS.PACK_SAVED'));
        },
        error: (err) => this.swal.error(this.messageOf(err, 'PACKS.ERROR_SAVE_PACK')),
      });
  }

  private loadFeatures(packId: number): void {
    this.packFeatures = new Set();
    this.featuresLoaded = false;
    this.featuresDirty = false;
    this.packsApi
      .getFeatures(packId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          // La réponse d'un pack quitté entre-temps ne doit pas cocher le suivant.
          if (this.selected?.id !== packId) return;
          this.packFeatures = new Set(res.data ?? []);
          this.featuresLoaded = true;
        },
        error: (err) => this.swal.error(this.messageOf(err, 'PACKS.FEATURES_ERROR_LOAD')),
      });
  }

  toggleFeature(key: string): void {
    const next = new Set(this.packFeatures);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    this.packFeatures = next;
    this.featuresDirty = true;
  }

  /** Comme dans « Profils et accès » : tout cocher, ou tout décocher s'il l'est déjà. */
  toggleFeatureModule(module: PickerModule): void {
    const keys = module.permissions.map((f) => f.key);
    const all = keys.every((k) => this.packFeatures.has(k));
    const next = new Set(this.packFeatures);
    for (const k of keys) {
      if (all) next.delete(k);
      else next.add(k);
    }
    this.packFeatures = next;
    this.featuresDirty = true;
  }

  saveFeatures(): void {
    if (!this.selected || !this.featuresLoaded) return;
    const packId = this.selected.id;

    this.isSavingFeatures = true;
    this.packsApi
      .replaceFeatures(packId, [...this.packFeatures])
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isSavingFeatures = false)),
      )
      .subscribe({
        next: (res) => {
          if (this.selected?.id === packId) {
            this.packFeatures = new Set(res.data ?? []);
            this.featuresDirty = false;
          }
          this.swal.success(this.translate.instant('PACKS.FEATURES_SAVED'));
        },
        error: (err) => this.swal.error(this.messageOf(err, 'PACKS.FEATURES_ERROR_SAVE')),
      });
  }

  private messageOf(err: unknown, fallbackKey: string): string {
    const message = (err as { error?: { message?: string } })?.error?.message;
    return message || this.translate.instant(fallbackKey);
  }
}
